import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, Image, Save, Eye, Upload, Trash2, RefreshCw,
  CheckCircle, AlertCircle, Clock, Folder, Search, X, Plus,
  Home, BookOpen, Users, Phone, HelpCircle, Heart, Edit, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Page icons mapping
const PAGE_ICONS = {
  homepage: Home,
  our_story: Heart,
  about_us: Users,
  programs: BookOpen,
  contact: Phone,
  faq: HelpCircle
};

export default function AdminCMSPage() {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pages');
  const [mediaSearch, setMediaSearch] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaCallback, setMediaCallback] = useState(null);
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);

  // Fetch all pages
  const fetchPages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  }, [token]);

  // Fetch page content
  const fetchPageContent = useCallback(async (pageKey) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${pageKey}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPageContent(data);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  }, [token]);

  // Fetch media assets
  const fetchMediaAssets = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/media/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMediaAssets(data.results || data);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPages(), fetchMediaAssets()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPages, fetchMediaAssets]);

  // Select a page for editing
  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    await fetchPageContent(page.page_key);
  };

  // Save draft content
  const handleSaveDraft = async () => {
    if (!selectedPage || !pageContent) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${selectedPage.page_key}/draft/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: pageContent.draft_content })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPageContent(data);
        await fetchPages();
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
    setSaving(false);
  };

  // Publish content
  const handlePublish = async () => {
    if (!selectedPage) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${selectedPage.page_key}/publish/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPageContent(data.page);
        await fetchPages();
      }
    } catch (error) {
      console.error('Error publishing:', error);
    }
    setSaving(false);
  };

  // Revert to published
  const handleRevert = async () => {
    if (!selectedPage) return;
    
    if (!window.confirm('Revert all draft changes to the published version?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${selectedPage.page_key}/revert/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPageContent(data.page);
        await fetchPages();
      }
    } catch (error) {
      console.error('Error reverting:', error);
    }
  };

  // Preview page
  const handlePreview = () => {
    if (!selectedPage) return;
    const previewUrl = selectedPage.page_key === 'homepage' ? '/' : `/${selectedPage.page_key.replace('_', '-')}`;
    window.open(`${previewUrl}?preview=true`, '_blank');
  };

  // Update content field
  const updateContent = (path, value) => {
    if (!pageContent) return;
    
    const newContent = JSON.parse(JSON.stringify(pageContent.draft_content));
    const keys = path.split('.');
    let obj = newContent;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (keys[i].includes('[')) {
        const [key, index] = keys[i].split('[');
        const idx = parseInt(index.replace(']', ''));
        obj = obj[key][idx];
      } else {
        obj = obj[keys[i]];
      }
    }
    
    const lastKey = keys[keys.length - 1];
    if (lastKey.includes('[')) {
      const [key, index] = lastKey.split('[');
      const idx = parseInt(index.replace(']', ''));
      obj[key][idx] = value;
    } else {
      obj[lastKey] = value;
    }
    
    setPageContent({ ...pageContent, draft_content: newContent });
  };

  // Handle media upload with preview
  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setUploadingMedia(true);
    
    // Create previews for all files
    const previews = [];
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        previews.push({ name: file.name, url: previewUrl, status: 'uploading' });
      } else {
        previews.push({ name: file.name, url: null, status: 'uploading', isDocument: true });
      }
    }
    setUploadPreviews(previews);
    
    // Upload each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('asset_type', file.type.startsWith('image/') ? 'image' : 'document');
      
      try {
        const res = await fetch(`${API_URL}/api/cms/media/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          // Update preview status
          setUploadPreviews(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'done' } : p
          ));
        } else {
          setUploadPreviews(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'error' } : p
          ));
        }
      } catch (error) {
        console.error('Error uploading media:', error);
        setUploadPreviews(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'error' } : p
        ));
      }
    }
    
    // Refresh media list - previews stay visible until cleared manually or new upload
    await fetchMediaAssets();
    
    // Clear previews only after all uploads are done and successful
    // Keep them visible so user can see the result
    setUploadPreviews(prev => {
      // Revoke object URLs for completed uploads to free memory
      prev.forEach(p => {
        if (p.status === 'done' && p.url) {
          URL.revokeObjectURL(p.url);
        }
      });
      // Clear successful uploads, keep errors visible
      return prev.filter(p => p.status === 'error');
    });
    
    setUploadingMedia(false);
    // Reset input
    e.target.value = '';
  };

  // Delete media
  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this media asset?')) return;
    
    try {
      await fetch(`${API_URL}/api/cms/media/${mediaId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMediaAssets();
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  };

  // Select media for content
  const selectMediaForContent = (callback) => {
    setMediaCallback(() => callback);
    setShowMediaModal(true);
  };

  // Filtered media
  const filteredMedia = mediaAssets.filter(m => 
    m.name.toLowerCase().includes(mediaSearch.toLowerCase())
  );

  // Render status badge
  const renderStatusBadge = (page) => {
    if (page.has_unpublished_changes) {
      return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock className="w-3 h-3 mr-1" /> Draft</Badge>;
    }
    if (page.status === 'published') {
      return <Badge variant="outline" className="text-green-600 border-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Published</Badge>;
    }
    return <Badge variant="outline" className="text-gray-600"><AlertCircle className="w-3 h-3 mr-1" /> {page.status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-rsc-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-cms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-rsc-navy">Content Management</h1>
          <p className="text-gray-500">Edit customer-facing website content</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pages" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Pages
          </TabsTrigger>
          <TabsTrigger value="media" className="flex items-center gap-2">
            <Image className="w-4 h-4" /> Media Library
          </TabsTrigger>
        </TabsList>

        {/* Pages Tab */}
        <TabsContent value="pages" className="mt-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Page List */}
            <div className="col-span-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Pages</CardTitle>
                  <CardDescription>Select a page to edit</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {pages.map(page => {
                      const Icon = PAGE_ICONS[page.page_key] || FileText;
                      return (
                        <button
                          key={page.id}
                          onClick={() => handleSelectPage(page)}
                          className={`w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                            selectedPage?.id === page.id ? 'bg-rsc-blue/5 border-l-4 border-rsc-blue' : ''
                          }`}
                          data-testid={`page-item-${page.page_key}`}
                        >
                          <Icon className="w-5 h-5 text-rsc-blue" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{page.page_name}</p>
                            <p className="text-xs text-gray-500">v{page.version}</p>
                          </div>
                          {renderStatusBadge(page)}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Page Editor */}
            <div className="col-span-8">
              {selectedPage && pageContent ? (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Edit className="w-5 h-5" />
                        Editing: {selectedPage.page_name}
                      </CardTitle>
                      <CardDescription>
                        {pageContent.has_unpublished_changes && (
                          <span className="text-yellow-600">You have unsaved changes</span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handlePreview} data-testid="preview-btn">
                        <Eye className="w-4 h-4 mr-2" /> Preview
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleRevert} disabled={!pageContent.has_unpublished_changes}>
                        <RefreshCw className="w-4 h-4 mr-2" /> Revert
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
                        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Draft'}
                      </Button>
                      <Button size="sm" onClick={handlePublish} disabled={saving || !pageContent.has_unpublished_changes} data-testid="publish-btn">
                        <CheckCircle className="w-4 h-4 mr-2" /> Publish
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Dynamic Content Editor based on page type */}
                    <PageContentEditor 
                      pageKey={selectedPage.page_key}
                      content={pageContent.draft_content}
                      updateContent={updateContent}
                      selectMedia={selectMediaForContent}
                    />
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Select a page from the list to start editing</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Media Library Tab */}
        <TabsContent value="media" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Media Library</CardTitle>
                <CardDescription>Upload and manage images and documents</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search media..."
                    value={mediaSearch}
                    onChange={(e) => setMediaSearch(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleMediaUpload}
                    className="hidden"
                    data-testid="media-upload-input"
                  />
                  <div className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2" data-testid="upload-media-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingMedia ? 'Uploading...' : 'Upload'}
                  </div>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {/* Upload Previews */}
              {uploadPreviews.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Uploading...</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {uploadPreviews.map((preview, idx) => (
                      <div key={idx} className="relative border-2 border-dashed border-blue-300 rounded-lg overflow-hidden bg-blue-50">
                        {preview.url ? (
                          <img src={preview.url} alt={preview.name} className="w-full h-32 object-cover" />
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center">
                            <FileText className="w-12 h-12 text-blue-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          {preview.status === 'uploading' && (
                            <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                          {preview.status === 'done' && (
                            <CheckCircle className="w-8 h-8 text-green-400" />
                          )}
                          {preview.status === 'error' && (
                            <AlertCircle className="w-8 h-8 text-red-400" />
                          )}
                        </div>
                        <div className="p-2 bg-white/90">
                          <p className="text-xs font-medium truncate">{preview.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Grid */}
              {filteredMedia.length === 0 && uploadPreviews.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No media assets yet</p>
                  <p className="text-sm">Upload images and documents to use in your pages</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredMedia.map(media => (
                    <div key={media.id} className="group relative border rounded-lg overflow-hidden bg-gray-50 cursor-pointer hover:ring-2 hover:ring-rsc-blue transition-all" onClick={() => setPreviewMedia(media)}>
                      {media.asset_type === 'image' ? (
                        <img 
                          src={media.url || media.file_url || media.file} 
                          alt={media.alt_text || media.name}
                          className="w-full h-32 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      <div className="p-2">
                        <p className="text-xs font-medium truncate">{media.name}</p>
                        <p className="text-xs text-gray-500">{media.asset_type}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMedia(media.id); }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute top-2 left-2 p-1 bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        <Eye className="w-3 h-3" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Preview Modal */}
      {previewMedia && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" 
          onClick={() => setPreviewMedia(null)}
          data-testid="media-preview-modal"
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setPreviewMedia(null)} 
              className="absolute -top-2 -right-2 p-2 bg-white rounded-full shadow-lg z-10 hover:bg-gray-100"
              data-testid="close-preview-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              {previewMedia.asset_type === 'image' ? (
                <img 
                  src={previewMedia.url || previewMedia.file_url || previewMedia.file} 
                  alt={previewMedia.alt_text || previewMedia.name}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle">Image not found</text></svg>'; }}
                />
              ) : (
                <div className="p-12 text-center">
                  <FileText className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                  <p className="font-medium">{previewMedia.name}</p>
                </div>
              )}
              <div className="p-4 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{previewMedia.name}</p>
                    <p className="text-sm text-gray-500">
                      {previewMedia.asset_type} 
                      {previewMedia.file_size ? ` • ${(previewMedia.file_size / 1024).toFixed(1)} KB` : ''}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const url = previewMedia.url || previewMedia.file_url || previewMedia.file;
                      navigator.clipboard.writeText(url);
                    }}
                  >
                    Copy URL
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Selection Modal */}
      {showMediaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Select Media</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowMediaModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-4 gap-4">
                {mediaAssets.filter(m => m.asset_type === 'image').map(media => (
                  <button
                    key={media.id}
                    onClick={() => {
                      if (mediaCallback) {
                        mediaCallback(media.url || media.file_url || media.file);
                      }
                      setShowMediaModal(false);
                    }}
                    className="border rounded-lg overflow-hidden hover:border-rsc-blue transition-colors"
                  >
                    <img 
                      src={media.url || media.file_url || media.file} 
                      alt={media.name}
                      className="w-full h-24 object-cover"
                    />
                    <p className="text-xs p-2 truncate">{media.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Page Content Editor Component
function PageContentEditor({ pageKey, content, updateContent, selectMedia }) {
  if (!content) return null;

  switch (pageKey) {
    case 'homepage':
      return <HomepageEditor content={content} updateContent={updateContent} selectMedia={selectMedia} />;
    case 'our_story':
      return <OurStoryEditor content={content} updateContent={updateContent} selectMedia={selectMedia} />;
    case 'about_us':
      return <AboutUsEditor content={content} updateContent={updateContent} selectMedia={selectMedia} />;
    case 'programs':
      return <ProgramsEditor content={content} updateContent={updateContent} selectMedia={selectMedia} />;
    case 'contact':
      return <ContactEditor content={content} updateContent={updateContent} />;
    case 'faq':
      return <FAQEditor content={content} updateContent={updateContent} />;
    default:
      return <GenericEditor content={content} updateContent={updateContent} />;
  }
}

// Homepage Editor
function HomepageEditor({ content, updateContent, selectMedia }) {
  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Home className="w-4 h-4" /> Hero Section
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
              data-testid="hero-title-input"
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
          <div>
            <Label>Primary CTA Text</Label>
            <Input
              value={content.hero?.cta_primary_text || ''}
              onChange={(e) => updateContent('hero.cta_primary_text', e.target.value)}
            />
          </div>
          <div>
            <Label>Primary CTA Link</Label>
            <Input
              value={content.hero?.cta_primary_link || ''}
              onChange={(e) => updateContent('hero.cta_primary_link', e.target.value)}
            />
          </div>
          <div>
            <Label>Secondary CTA Text</Label>
            <Input
              value={content.hero?.cta_secondary_text || ''}
              onChange={(e) => updateContent('hero.cta_secondary_text', e.target.value)}
            />
          </div>
          <div>
            <Label>Secondary CTA Link</Label>
            <Input
              value={content.hero?.cta_secondary_link || ''}
              onChange={(e) => updateContent('hero.cta_secondary_link', e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label>Background Image</Label>
            <div className="flex gap-2 items-center mt-1">
              <Input
                value={content.hero?.background_image || ''}
                onChange={(e) => updateContent('hero.background_image', e.target.value)}
                placeholder="Enter URL or select from library"
                className="flex-1"
              />
              <Button variant="outline" size="sm" onClick={() => selectMedia((url) => updateContent('hero.background_image', url))}>
                <Image className="w-4 h-4 mr-1" /> Select
              </Button>
            </div>
            {content.hero?.background_image && (
              <div className="mt-2 relative w-32 h-20 rounded overflow-hidden border">
                <img src={content.hero.background_image} alt="Hero bg" className="w-full h-full object-cover" />
                <button onClick={() => updateContent('hero.background_image', null)} className="absolute top-1 right-1 bg-red-500 text-white rounded p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Features Section</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input
                value={content.features?.title || ''}
                onChange={(e) => updateContent('features.title', e.target.value)}
              />
            </div>
            <div>
              <Label>Section Subtitle</Label>
              <Input
                value={content.features?.subtitle || ''}
                onChange={(e) => updateContent('features.subtitle', e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <Label>Feature Items</Label>
            {content.features?.items?.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-start bg-gray-50 p-3 rounded">
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Icon (e.g., GraduationCap)"
                    value={item.icon || ''}
                    onChange={(e) => {
                      const items = [...content.features.items];
                      items[idx] = { ...items[idx], icon: e.target.value };
                      updateContent('features.items', items);
                    }}
                  />
                  <Input
                    placeholder="Title"
                    value={item.title || ''}
                    onChange={(e) => {
                      const items = [...content.features.items];
                      items[idx] = { ...items[idx], title: e.target.value };
                      updateContent('features.items', items);
                    }}
                  />
                  <Input
                    placeholder="Description"
                    value={item.description || ''}
                    onChange={(e) => {
                      const items = [...content.features.items];
                      items[idx] = { ...items[idx], description: e.target.value };
                      updateContent('features.items', items);
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const items = content.features.items.filter((_, i) => i !== idx);
                    updateContent('features.items', items);
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const items = [...(content.features?.items || []), { icon: '', title: '', description: '' }];
                updateContent('features.items', items);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Feature
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Stats Section</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.stats?.visible ?? true}
              onChange={(e) => updateContent('stats.visible', e.target.checked)}
            />
            <span className="text-sm">Visible</span>
          </label>
        </div>
        <div className="space-y-3">
          {content.stats?.items?.map((stat, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <Input
                placeholder="Value (e.g., 5000+)"
                value={stat.value || ''}
                onChange={(e) => {
                  const items = [...content.stats.items];
                  items[idx] = { ...items[idx], value: e.target.value };
                  updateContent('stats.items', items);
                }}
                className="flex-1"
              />
              <Input
                placeholder="Label"
                value={stat.label || ''}
                onChange={(e) => {
                  const items = [...content.stats.items];
                  items[idx] = { ...items[idx], label: e.target.value };
                  updateContent('stats.items', items);
                }}
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => {
                const items = content.stats.items.filter((_, i) => i !== idx);
                updateContent('stats.items', items);
              }}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const items = [...(content.stats?.items || []), { value: '', label: '' }];
            updateContent('stats.items', items);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Stat
          </Button>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Testimonials Section</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.testimonials?.visible ?? true}
              onChange={(e) => updateContent('testimonials.visible', e.target.checked)}
            />
            <span className="text-sm">Visible</span>
          </label>
        </div>
        <div className="mb-4">
          <Label>Section Title</Label>
          <Input
            value={content.testimonials?.title || ''}
            onChange={(e) => updateContent('testimonials.title', e.target.value)}
            placeholder="What Parents Say"
          />
        </div>
        <div className="space-y-4">
          <Label>Testimonials</Label>
          {content.testimonials?.items?.map((item, idx) => (
            <div key={idx} className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex gap-3 items-start">
                <div className="flex-shrink-0">
                  {item.image ? (
                    <div className="relative">
                      <img src={item.image} alt={item.author} className="w-16 h-16 rounded-full object-cover" />
                      <button onClick={() => {
                        const items = [...content.testimonials.items];
                        items[idx] = { ...items[idx], image: null };
                        updateContent('testimonials.items', items);
                      }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => selectMedia((url) => {
                      const items = [...content.testimonials.items];
                      items[idx] = { ...items[idx], image: url };
                      updateContent('testimonials.items', items);
                    })} className="w-16 h-16 rounded-full">
                      <Image className="w-6 h-6" />
                    </Button>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <textarea
                    placeholder="Quote"
                    value={item.quote || ''}
                    onChange={(e) => {
                      const items = [...content.testimonials.items];
                      items[idx] = { ...items[idx], quote: e.target.value };
                      updateContent('testimonials.items', items);
                    }}
                    className="w-full p-2 border rounded text-sm"
                    rows={2}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Author Name"
                      value={item.author || ''}
                      onChange={(e) => {
                        const items = [...content.testimonials.items];
                        items[idx] = { ...items[idx], author: e.target.value };
                        updateContent('testimonials.items', items);
                      }}
                    />
                    <Input
                      placeholder="Role (e.g., Parent)"
                      value={item.role || ''}
                      onChange={(e) => {
                        const items = [...content.testimonials.items];
                        items[idx] = { ...items[idx], role: e.target.value };
                        updateContent('testimonials.items', items);
                      }}
                    />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  const items = content.testimonials.items.filter((_, i) => i !== idx);
                  updateContent('testimonials.items', items);
                }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const items = [...(content.testimonials?.items || []), { quote: '', author: '', role: 'Parent', image: null }];
            updateContent('testimonials.items', items);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Testimonial
          </Button>
        </div>
      </div>

      {/* Partners/Logos Section */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Partner Logos Section</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.partners?.visible ?? true}
              onChange={(e) => updateContent('partners.visible', e.target.checked)}
            />
            <span className="text-sm">Visible</span>
          </label>
        </div>
        <div className="mb-4">
          <Label>Section Title</Label>
          <Input
            value={content.partners?.title || ''}
            onChange={(e) => updateContent('partners.title', e.target.value)}
            placeholder="Trusted By"
          />
        </div>
        <div className="space-y-4">
          <Label>Partner Logos</Label>
          <div className="grid grid-cols-4 gap-4">
            {content.partners?.logos?.map((logo, idx) => (
              <div key={idx} className="bg-gray-50 p-3 rounded-lg text-center relative group">
                {logo.image ? (
                  <>
                    <img src={logo.image} alt={logo.name || 'Partner'} className="h-12 mx-auto object-contain mb-2" />
                    <Input
                      placeholder="Partner Name"
                      value={logo.name || ''}
                      onChange={(e) => {
                        const logos = [...content.partners.logos];
                        logos[idx] = { ...logos[idx], name: e.target.value };
                        updateContent('partners.logos', logos);
                      }}
                      className="text-xs"
                    />
                    <button onClick={() => {
                      const logos = content.partners.logos.filter((_, i) => i !== idx);
                      updateContent('partners.logos', logos);
                    }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => selectMedia((url) => {
                    const logos = [...content.partners.logos];
                    logos[idx] = { ...logos[idx], image: url };
                    updateContent('partners.logos', logos);
                  })} className="w-full h-16">
                    <Image className="w-6 h-6" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => {
            const logos = [...(content.partners?.logos || []), { name: '', image: null, url: '' }];
            updateContent('partners.logos', logos);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Partner Logo
          </Button>
        </div>
      </div>

      {/* CTA Section */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Call to Action Section</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.cta_section?.visible ?? true}
              onChange={(e) => updateContent('cta_section.visible', e.target.checked)}
            />
            <span className="text-sm">Visible</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.cta_section?.title || ''}
              onChange={(e) => updateContent('cta_section.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.cta_section?.subtitle || ''}
              onChange={(e) => updateContent('cta_section.subtitle', e.target.value)}
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input
              value={content.cta_section?.cta_text || ''}
              onChange={(e) => updateContent('cta_section.cta_text', e.target.value)}
            />
          </div>
          <div>
            <Label>Button Link</Label>
            <Input
              value={content.cta_section?.cta_link || ''}
              onChange={(e) => updateContent('cta_section.cta_link', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Our Story Editor
function OurStoryEditor({ content, updateContent }) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Hero Section</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label>Tagline</Label>
            <textarea
              value={content.hero?.tagline || ''}
              onChange={(e) => updateContent('hero.tagline', e.target.value)}
              className="w-full p-2 border rounded-md"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Story Sections */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Story Sections</h3>
        <div className="space-y-4">
          {content.sections?.map((section, idx) => (
            <div key={section.id || idx} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-sm">{section.title}</span>
                <select
                  value={section.background || 'white'}
                  onChange={(e) => {
                    const sections = [...content.sections];
                    sections[idx] = { ...sections[idx], background: e.target.value };
                    updateContent('sections', sections);
                  }}
                  className="text-sm border rounded px-2 py-1"
                >
                  <option value="white">White</option>
                  <option value="light">Light</option>
                  <option value="navy">Navy</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Input
                    value={section.icon || ''}
                    onChange={(e) => {
                      const sections = [...content.sections];
                      sections[idx] = { ...sections[idx], icon: e.target.value };
                      updateContent('sections', sections);
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={section.title || ''}
                    onChange={(e) => {
                      const sections = [...content.sections];
                      sections[idx] = { ...sections[idx], title: e.target.value };
                      updateContent('sections', sections);
                    }}
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">Content</Label>
                <textarea
                  value={section.content || ''}
                  onChange={(e) => {
                    const sections = [...content.sections];
                    sections[idx] = { ...sections[idx], content: e.target.value };
                    updateContent('sections', sections);
                  }}
                  className="w-full p-2 border rounded-md text-sm"
                  rows={3}
                />
              </div>
              {section.quote !== undefined && (
                <div className="mt-3">
                  <Label className="text-xs">Quote</Label>
                  <Input
                    value={section.quote || ''}
                    onChange={(e) => {
                      const sections = [...content.sections];
                      sections[idx] = { ...sections[idx], quote: e.target.value };
                      updateContent('sections', sections);
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// About Us Editor
function AboutUsEditor({ content, updateContent, selectMedia }) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Hero Section</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Mission */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Mission</h3>
        <div>
          <Label>Title</Label>
          <Input
            value={content.mission?.title || ''}
            onChange={(e) => updateContent('mission.title', e.target.value)}
          />
        </div>
        <div className="mt-3">
          <Label>Content</Label>
          <textarea
            value={content.mission?.content || ''}
            onChange={(e) => updateContent('mission.content', e.target.value)}
            className="w-full p-2 border rounded-md"
            rows={3}
          />
        </div>
      </div>

      {/* Vision */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Vision</h3>
        <div>
          <Label>Title</Label>
          <Input
            value={content.vision?.title || ''}
            onChange={(e) => updateContent('vision.title', e.target.value)}
          />
        </div>
        <div className="mt-3">
          <Label>Content</Label>
          <textarea
            value={content.vision?.content || ''}
            onChange={(e) => updateContent('vision.content', e.target.value)}
            className="w-full p-2 border rounded-md"
            rows={3}
          />
        </div>
      </div>

      {/* Values */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Values</h3>
        <div className="mb-4">
          <Label>Section Title</Label>
          <Input
            value={content.values?.title || ''}
            onChange={(e) => updateContent('values.title', e.target.value)}
          />
        </div>
        <div className="space-y-3">
          {content.values?.items?.map((value, idx) => (
            <div key={idx} className="flex gap-2 items-start bg-gray-50 p-3 rounded">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input
                  placeholder="Title"
                  value={value.title || ''}
                  onChange={(e) => {
                    const items = [...content.values.items];
                    items[idx] = { ...items[idx], title: e.target.value };
                    updateContent('values.items', items);
                  }}
                />
                <Input
                  placeholder="Description"
                  value={value.description || ''}
                  onChange={(e) => {
                    const items = [...content.values.items];
                    items[idx] = { ...items[idx], description: e.target.value };
                    updateContent('values.items', items);
                  }}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => {
                const items = content.values.items.filter((_, i) => i !== idx);
                updateContent('values.items', items);
              }}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const items = [...(content.values?.items || []), { title: '', description: '' }];
            updateContent('values.items', items);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Value
          </Button>
        </div>
      </div>

      {/* Team Section */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Team Section</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.team?.visible ?? true}
              onChange={(e) => updateContent('team.visible', e.target.checked)}
            />
            <span className="text-sm">Visible</span>
          </label>
        </div>
        <div className="mb-4">
          <Label>Section Title</Label>
          <Input
            value={content.team?.title || ''}
            onChange={(e) => updateContent('team.title', e.target.value)}
            placeholder="Our Leadership"
          />
        </div>
        <div className="space-y-4">
          <Label>Team Members</Label>
          {content.team?.members?.map((member, idx) => (
            <div key={idx} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex gap-3 items-start">
                <div className="flex-shrink-0">
                  {member.image ? (
                    <div className="relative">
                      <img src={member.image} alt={member.name} className="w-20 h-20 rounded-full object-cover" />
                      <button onClick={() => {
                        const members = [...content.team.members];
                        members[idx] = { ...members[idx], image: null };
                        updateContent('team.members', members);
                      }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => selectMedia((url) => {
                      const members = [...content.team.members];
                      members[idx] = { ...members[idx], image: url };
                      updateContent('team.members', members);
                    })} className="w-20 h-20 rounded-full">
                      <Image className="w-6 h-6" />
                    </Button>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder="Name"
                    value={member.name || ''}
                    onChange={(e) => {
                      const members = [...content.team.members];
                      members[idx] = { ...members[idx], name: e.target.value };
                      updateContent('team.members', members);
                    }}
                  />
                  <Input
                    placeholder="Role / Title"
                    value={member.role || ''}
                    onChange={(e) => {
                      const members = [...content.team.members];
                      members[idx] = { ...members[idx], role: e.target.value };
                      updateContent('team.members', members);
                    }}
                  />
                  <textarea
                    placeholder="Bio (optional)"
                    value={member.bio || ''}
                    onChange={(e) => {
                      const members = [...content.team.members];
                      members[idx] = { ...members[idx], bio: e.target.value };
                      updateContent('team.members', members);
                    }}
                    className="w-full p-2 border rounded text-sm"
                    rows={2}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  const members = content.team.members.filter((_, i) => i !== idx);
                  updateContent('team.members', members);
                }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const members = [...(content.team?.members || []), { name: '', role: '', bio: '', image: null }];
            updateContent('team.members', members);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Team Member
          </Button>
        </div>
      </div>
    </div>
  );
}

// Programs Editor  
function ProgramsEditor({ content, updateContent, selectMedia }) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Hero Section</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Programs */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Programs</h3>
        <div className="space-y-4">
          {content.programs?.map((program, idx) => (
            <div key={program.id || idx} className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={program.title || ''}
                    onChange={(e) => {
                      const programs = [...content.programs];
                      programs[idx] = { ...programs[idx], title: e.target.value };
                      updateContent('programs', programs);
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Link</Label>
                  <Input
                    value={program.link || ''}
                    onChange={(e) => {
                      const programs = [...content.programs];
                      programs[idx] = { ...programs[idx], link: e.target.value };
                      updateContent('programs', programs);
                    }}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={program.description || ''}
                    onChange={(e) => {
                      const programs = [...content.programs];
                      programs[idx] = { ...programs[idx], description: e.target.value };
                      updateContent('programs', programs);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const programs = [...(content.programs || []), { id: Date.now(), title: '', description: '', link: '', image: null }];
            updateContent('programs', programs);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Program
          </Button>
        </div>
      </div>
    </div>
  );
}

// Contact Editor
function ContactEditor({ content, updateContent }) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Hero Section</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Contact Information</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Email</Label>
            <Input
              value={content.info?.email || ''}
              onChange={(e) => updateContent('info.email', e.target.value)}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={content.info?.phone || ''}
              onChange={(e) => updateContent('info.phone', e.target.value)}
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={content.info?.address || ''}
              onChange={(e) => updateContent('info.address', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Office Hours */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Office Hours</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Weekdays</Label>
            <Input
              value={content.office_hours?.weekdays || ''}
              onChange={(e) => updateContent('office_hours.weekdays', e.target.value)}
            />
          </div>
          <div>
            <Label>Saturday</Label>
            <Input
              value={content.office_hours?.saturday || ''}
              onChange={(e) => updateContent('office_hours.saturday', e.target.value)}
            />
          </div>
          <div>
            <Label>Sunday</Label>
            <Input
              value={content.office_hours?.sunday || ''}
              onChange={(e) => updateContent('office_hours.sunday', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Social Media Links</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Facebook</Label>
            <Input
              value={content.social?.facebook || ''}
              onChange={(e) => updateContent('social.facebook', e.target.value)}
              placeholder="https://facebook.com/..."
            />
          </div>
          <div>
            <Label>Twitter</Label>
            <Input
              value={content.social?.twitter || ''}
              onChange={(e) => updateContent('social.twitter', e.target.value)}
              placeholder="https://twitter.com/..."
            />
          </div>
          <div>
            <Label>Instagram</Label>
            <Input
              value={content.social?.instagram || ''}
              onChange={(e) => updateContent('social.instagram', e.target.value)}
              placeholder="https://instagram.com/..."
            />
          </div>
          <div>
            <Label>LinkedIn</Label>
            <Input
              value={content.social?.linkedin || ''}
              onChange={(e) => updateContent('social.linkedin', e.target.value)}
              placeholder="https://linkedin.com/..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// FAQ Editor
function FAQEditor({ content, updateContent }) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Hero Section</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.hero?.title || ''}
              onChange={(e) => updateContent('hero.title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.hero?.subtitle || ''}
              onChange={(e) => updateContent('hero.subtitle', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* FAQ Categories */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">FAQ Categories</h3>
        <div className="space-y-4">
          {content.categories?.map((category, catIdx) => (
            <div key={catIdx} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Input
                  value={category.name || ''}
                  onChange={(e) => {
                    const categories = [...content.categories];
                    categories[catIdx] = { ...categories[catIdx], name: e.target.value };
                    updateContent('categories', categories);
                  }}
                  className="font-medium w-48"
                  placeholder="Category Name"
                />
                <Button variant="ghost" size="sm" onClick={() => {
                  const categories = content.categories.filter((_, i) => i !== catIdx);
                  updateContent('categories', categories);
                }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              
              <div className="space-y-2 ml-4">
                {category.items?.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white p-3 rounded border">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder="Question"
                          value={item.question || ''}
                          onChange={(e) => {
                            const categories = [...content.categories];
                            categories[catIdx].items[itemIdx] = { ...categories[catIdx].items[itemIdx], question: e.target.value };
                            updateContent('categories', categories);
                          }}
                          className="mb-2"
                        />
                        <textarea
                          placeholder="Answer"
                          value={item.answer || ''}
                          onChange={(e) => {
                            const categories = [...content.categories];
                            categories[catIdx].items[itemIdx] = { ...categories[catIdx].items[itemIdx], answer: e.target.value };
                            updateContent('categories', categories);
                          }}
                          className="w-full p-2 border rounded-md text-sm"
                          rows={2}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => {
                        const categories = [...content.categories];
                        categories[catIdx].items = categories[catIdx].items.filter((_, i) => i !== itemIdx);
                        updateContent('categories', categories);
                      }}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => {
                  const categories = [...content.categories];
                  categories[catIdx].items = [...(categories[catIdx].items || []), { question: '', answer: '' }];
                  updateContent('categories', categories);
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add FAQ Item
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const categories = [...(content.categories || []), { name: 'New Category', items: [] }];
            updateContent('categories', categories);
          }}>
            <Plus className="w-4 h-4 mr-2" /> Add Category
          </Button>
        </div>
      </div>
    </div>
  );
}

// Generic JSON Editor fallback
function GenericEditor({ content, updateContent }) {
  const [jsonString, setJsonString] = useState(JSON.stringify(content, null, 2));
  const [error, setError] = useState(null);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(jsonString);
      Object.keys(parsed).forEach(key => {
        updateContent(key, parsed[key]);
      });
      setError(null);
    } catch (e) {
      setError('Invalid JSON');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          This page type doesn't have a custom editor. You can edit the raw JSON content below.
        </p>
      </div>
      <textarea
        value={jsonString}
        onChange={(e) => setJsonString(e.target.value)}
        className="w-full h-96 p-4 font-mono text-sm border rounded-lg"
      />
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <Button onClick={handleSave}>Apply Changes</Button>
    </div>
  );
}
